import { API_BASE_URL, CONNECT_URL, CONNECT_CLIENT_ID } from '../config';

const STATE_KEY = 'globedata.oauthState';
const RETURN_KEY = 'globedata.oauthReturnTo';
// Set by a logout that could not end Connect's SSO session: the next boot shows the login
// screen instead of bouncing, or the user would be signed straight back in.
const LOGGED_OUT_KEY = 'globedata.loggedOut';

// Full-page login: the backend builds the PKCE challenge, the browser goes to Connect. No
// click involved: a missing session always ends here. One start per page: concurrent callers
// (parallel 401s) would each overwrite the PKCE cookie and the stored state, and the navigation
// that wins would match neither. Once a start succeeded the page is leaving, so later callers
// share that promise; only a failure lets a new start through.
let starting = null;
export const startLogin = () => {
  if (!starting) {
    starting = (async () => {
      const res = await fetch(`${API_BASE_URL}/auth/start`, { credentials: 'include' });
      if (!res.ok) throw new Error(`auth/start ${res.status}`);
      const { authorize_url: authorizeUrl, state } = await res.json();
      sessionStorage.setItem(STATE_KEY, state);
      sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search + window.location.hash);
      window.location.assign(authorizeUrl);
    })().catch((error) => {
      starting = null;
      throw error;
    });
  }
  return starting;
};

export const markLoggedOut = () => sessionStorage.setItem(LOGGED_OUT_KEY, '1');

// Reads and clears the marker: it holds one boot back, not every later one.
export const takeLoggedOut = () => {
  const wasLoggedOut = Boolean(sessionStorage.getItem(LOGGED_OUT_KEY));
  sessionStorage.removeItem(LOGGED_OUT_KEY);
  return wasLoggedOut;
};

// What /auth/callback needs, read once: the state to compare and where to land afterwards.
export const takeCallbackContext = () => {
  const state = sessionStorage.getItem(STATE_KEY);
  const stored = sessionStorage.getItem(RETURN_KEY) || '/';
  const returnTo = /^\/(?![/\\])/.test(stored) && !stored.startsWith('/auth/callback') ? stored : '/';
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  return { state, returnTo };
};

export const exchangeCode = async ({ code, state }) => {
  const res = await fetch(`${API_BASE_URL}/auth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) throw new Error(`auth/exchange ${res.status}`);
  return res.json(); // { access_token, expires_in, user }
};

// Refresh tokens rotate: two tabs refreshing at once would present the same token, and the
// loser's 400 would clear the cookie the winner just set. Serialise across the tabs of this
// origin when the browser offers Web Locks; a single tab needs no lock.
const withRefreshLock = (fn) =>
  typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request('globedata-refresh', fn) : fn();

// null = no session (401); anything else that is not ok is Connect being unavailable.
// One refresh at a time per tab: concurrent callers share the same promise (Web Locks are
// absent on plain http, so the lock alone would let two calls present the same token).
let inflight = null;
export const refreshAccessToken = () => {
  if (!inflight) {
    inflight = withRefreshLock(async () => {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`auth/refresh ${res.status}`);
      return res.json(); // { access_token, expires_in }
    }).finally(() => {
      inflight = null;
    });
  }
  return inflight;
};

// Same scheme and same registrable domain (last two DNS labels): the browser's same-site rule is
// schemeful, so http://globedata… and https://connect… are two sites. connectSid is SameSite=Lax:
// a cross-site fetch never carries it, and Connect then answers 200 having ended nothing. Production
// Connect must never receive another environment's logout either.
const isSameSite = (a, b) => {
  const tail = (host) => host.split('.').slice(-2).join('.');
  try {
    const [x, y] = [new URL(a), new URL(b)];
    return x.protocol === y.protocol && tail(x.hostname) === tail(y.hostname);
  } catch {
    return false;
  }
};

// The app's refresh chain AND the shared SSO session: without the second, the next login
// signs the user back in silently. True only when the Connect logout succeeded: a Connect 5xx,
// a network failure or a skipped call must show the login screen, never sign the user straight back in.
export const endAllSessions = async () => {
  const ssoAttempted = Boolean(CONNECT_URL) && isSameSite(CONNECT_URL, window.location.href);
  const results = await Promise.allSettled([
    fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' }),
    ssoAttempted
      ? fetch(`${CONNECT_URL}/oauth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ client_id: CONNECT_CLIENT_ID }),
        })
      : Promise.resolve(),
  ]);
  return ssoAttempted && results[1].status === 'fulfilled' && Boolean(results[1].value && results[1].value.ok);
};

// The legacy credential was base64(login:password), in localStorage and in a cookie. Nothing
// reads it here any more; purge it on every mount. Host-only cookie: a .globe-groupe.com delete
// could hit Connect's own cookie of the same name.
export const purgeLegacyToken = () => {
  localStorage.removeItem('authToken');
  document.cookie = 'authToken=; path=/; max-age=-1';
};
